/**
 * Pure, DOM-free controller for the Sprint 9 `archetype_card_viewed`
 * beacon (ADR-017 §A.2). The React island in
 * components/marketing/archetype-card-beacon.tsx is a thin wrapper around
 * this so the fire rules (threshold, debounce, per-session dedup) are unit-
 * testable in vitest's node environment without jsdom or testing-library.
 *
 * Fire rules:
 *   - The card must be ≥ 50% in view (caller supplies `intersectionRatio`,
 *     matched against `VIEW_THRESHOLD`).
 *   - It must stay in view for ≥ `DEBOUNCE_MS` before the event fires (the
 *     caller drives a timer; this controller only starts/cancels it).
 *   - At most one event per slug per session, deduped via `sessionStorage`
 *     key `archetype_card_viewed:<slug>`.
 *
 * Destination is Plausible only, via the injected `dispatch`. The
 * controller never touches Meta CAPI or the DB.
 */

import type { Angle } from "@/lib/content/homepage-variants";

/** ≥ 50% of the card in view (ADR-017 §A.2). */
export const VIEW_THRESHOLD = 0.5;
/** Sustained in-view time before firing, so a fast scroll-past is ignored. */
export const DEBOUNCE_MS = 250;
export const SESSION_KEY_PREFIX = "archetype_card_viewed:";

export interface CardViewPayload {
  slug: string;
  position: number;
  variant_id: string;
  angle: Angle;
}

export interface CardViewInput {
  slug: string;
  position: number;
  variantId: string;
  angle: Angle;
}

/** Minimal sessionStorage surface — lets tests inject a fake store. */
export interface SessionLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface CardViewTrackerDeps {
  /** Sends the event to Plausible. */
  dispatch: (payload: CardViewPayload) => void;
  /** Per-session dedup store. Optional — falls back to no dedup if absent. */
  session?: SessionLike | null;
  /** Schedules the debounce timer; defaults to setTimeout. */
  setTimer?: (cb: () => void, ms: number) => number;
  /** Cancels the debounce timer; defaults to clearTimeout. */
  clearTimer?: (handle: number) => void;
}

export function sessionKeyFor(slug: string): string {
  return `${SESSION_KEY_PREFIX}${slug}`;
}

/**
 * Create a tracker for one card. The caller pushes intersection updates via
 * `onIntersection(isIntersecting, ratio)`; the tracker fires `dispatch` at
 * most once, after the card has been ≥ 50% in view for `DEBOUNCE_MS`.
 *
 * Returns `{ onIntersection, dispose, hasFired }`. `dispose` clears any
 * pending timer (call it on unmount).
 */
export function createCardViewTracker(
  input: CardViewInput,
  deps: CardViewTrackerDeps,
) {
  const { slug, position, variantId, angle } = input;
  const session = deps.session ?? null;
  const setTimer =
    deps.setTimer ??
    ((cb, ms) => (setTimeout(cb, ms) as unknown as number));
  const clearTimer = deps.clearTimer ?? ((h) => clearTimeout(h));

  const key = sessionKeyFor(slug);
  let fired = false;
  let timer: number | undefined;

  function alreadyCounted(): boolean {
    if (!session) return false;
    try {
      return session.getItem(key) === "1";
    } catch {
      return false;
    }
  }

  function markCounted(): void {
    if (!session) return;
    try {
      session.setItem(key, "1");
    } catch {
      /* private mode / quota — accept rare double-counts */
    }
  }

  function fire(): void {
    if (fired) return;
    fired = true;
    timer = undefined;
    dispatchAndMark();
  }

  function dispatchAndMark(): void {
    deps.dispatch({ slug, position, variant_id: variantId, angle });
    markCounted();
  }

  function cancelTimer(): void {
    if (timer !== undefined) {
      clearTimer(timer);
      timer = undefined;
    }
  }

  return {
    onIntersection(isIntersecting: boolean, ratio: number): void {
      if (fired || alreadyCounted()) return;
      const inView = isIntersecting && ratio >= VIEW_THRESHOLD;
      if (inView) {
        if (timer === undefined) {
          timer = setTimer(fire, DEBOUNCE_MS);
        }
      } else {
        cancelTimer();
      }
    },
    dispose(): void {
      cancelTimer();
    },
    hasFired(): boolean {
      return fired;
    },
  };
}
